import { all, takeLatest, call, select, put } from "redux-saga/effects";
import { DataLayer } from "@grandvisionhq/analytics";
import { Actions } from "@grandvisionhq/configurator";
import { actions } from "@grandvisionhq/state";
import "../../../state/actions.js";
import { updatePrescriptionValue, updatePrescriptionType, selectLensTypeSuccess, selectPackageSuccess, selectAddOnSuccess, resetSelectedAddOn } from "../../../configurations/state/configurations/actions.js";
import { fileUploadResult } from "./actions.js";
import "../../../add-to-cart/state/add-to-cart/selectors.js";
import "../../../bundles/state/bundles/selectors.js";
import "../../../bundles/state/bundles/reducer.js";
import { getSelectedLensType, getSelectedPackage, getSelectedAddOn, getBundlePrice, getPrescriptionType, hasVisitedStepPrescription, getSelectedFrameProductType } from "../../../configurator/state/configurator/selectors.js";
import "../../../configurator/state/configurator/reducer.js";
import "../../../products/state/products/selectors.js";
import "../../../products/state/products/reducer.js";
import { getTealiumSagas } from "../../../tealium/state/tealium/sagas.js";
import { getProductConfig, getConfigFlow, getUser } from "./selectors.internal.js";
import { fetchLensTypesSuccess } from "../../../bundles/state/bundles/actions.js";
function isSun(productType) {
  if (typeof productType === "string") {
    return productType === "Sunglasses";
  }
  return productType.obj?.name === "Sunglasses";
}
const getEventType = (p) => {
  const productType = typeof p === "string" ? p : p.productType;
  return isSun(productType) ? "RxSunConfigEvent" : "RxGlassesConfigEvent";
};
function* onFetchLensTypesSuccess(id) {
  const selectedLensType = yield select(getSelectedLensType);
  const selectedLensPackage = yield select(getSelectedPackage);
  const selectedLensAddOn = yield select(getSelectedAddOn);
  const bundlePrice = yield select(getBundlePrice, { id });
  const prescriptionType = yield select(getPrescriptionType);
  const rxConfigData = {
    lens_type: selectedLensType?.title,
    lens_variant: selectedLensType?.colorCode,
    lens_package: selectedLensPackage?.title,
    "add-ons": selectedLensAddOn?.title,
    price: bundlePrice,
    prescription_input_method: prescriptionType
  };
  const onConfiguratorOpenEvent = {
    event: "rx_open_click",
    event_category: id === "rxSun" ? "rx_sunglasses" : "rx_glasses",
    funnel: {
      version: "AAB3.0",
      type: "rx_config",
      // hardcoded value for datalayer team, this is the initial step before configurator flow starts
      step_index: 99,
      step_name: "Choose lenses"
    },
    rx_config: rxConfigData
  };
  yield put(DataLayer.Actions.pushEvent(onConfiguratorOpenEvent));
}
function* onPushEvent(id, eventAction, eventLabel) {
  const productConfig = yield select(getProductConfig, { id });
  const configFlow = yield select(getConfigFlow, { id });
  const user = yield select(getUser);
  const hasVisitedStepPrescription$1 = yield select(
    hasVisitedStepPrescription,
    { id }
  );
  const productType = yield select(
    getSelectedFrameProductType
  );
  if (productType !== "Frames" && productType !== "Sunglasses") {
    console.error(
      `Did not receive a valid product type for pushing an event to the data layer. Expected "Frames" or "Sunglasses", received: ${productType}`
    );
    return;
  }
  const DATA_KEY = isSun(productType) ? "RxSun" : "RxGlasses";
  const action = DataLayer.Actions.pushEvent({
    event: getEventType(productType),
    eventAction,
    eventLabel,
    ...hasVisitedStepPrescription$1 && user ? { user } : {},
    [DATA_KEY]: {
      configFlow,
      productConfig
    }
  });
  yield put(action);
}
const createSelectionHandler = (id, eventAction) => function* () {
  yield call(onPushEvent, id, eventAction);
};
const manualLensTypeSelection = (a) => actions.isType(a, selectLensTypeSuccess) && a.payload.reason === "user selection";
const manualLensPackageSelection = (a) => actions.isType(a, selectPackageSuccess) && a.payload.reason === "user selection";
const manualAddOnSelection = (a) => (actions.isType(a, selectAddOnSuccess) || actions.isType(a, resetSelectedAddOn)) && a.payload.reason === "user selection";
function* onViewStep({ payload: { id } }) {
  yield call(onPushEvent, id, "view step");
}
function* onUpdatePrescriptionValue(id, { payload: { parameter, side } }) {
  yield call(onPushEvent, id, "select rx attribute", `${parameter} ${side}`);
}
function* onUpdatePrescriptionType(id, { payload: { prescriptionType, reason } }) {
  if (reason === "default selection") {
    return;
  }
  const rxInputMethodMapping = {
    NewPrescription: {
      eventAction: "manual prescription",
      eventLabel: "manual"
    },
    EmailPrescription: {
      eventAction: "email prescription",
      eventLabel: "email"
    },
    ExistingCustomer: {
      eventAction: "existing customer",
      eventLabel: "customer"
    },
    PrescriptionFile: {
      eventAction: "upload prescription",
      eventLabel: "upload"
    },
    NewPrescriptionAndUpload: {
      eventAction: "manual prescription",
      eventLabel: "manual"
    },
    "book-appointment": {
      eventAction: "book-appointment",
      eventLabel: "book-appointment"
    }
  };
  const event = rxInputMethodMapping[prescriptionType];
  if (!event) {
    return;
  }
  yield call(onPushEvent, id, event.eventAction, event.eventLabel);
}
function* onFileUploadResult(id, { result }) {
  if (result) {
    yield call(onPushEvent, id, "upload message", result);
  }
}
function* onNextStep({ payload: { id } }) {
  yield call(onPushEvent, id, "click on button", "next step");
}
const getConfigurationChangesSagas = (id) => [
  takeLatest(
    updatePrescriptionValue.type,
    (a) => onUpdatePrescriptionValue(id, a)
  ),
  takeLatest(
    updatePrescriptionType.type,
    (a) => onUpdatePrescriptionType(id, a)
  ),
  // upload
  takeLatest(
    fileUploadResult.type,
    (a) => onFileUploadResult(id, a)
  ),
  takeLatest(manualLensTypeSelection, createSelectionHandler(id, "select lens type")),
  takeLatest(manualLensPackageSelection, createSelectionHandler(id, "select lens package")),
  takeLatest(manualAddOnSelection, createSelectionHandler(id, "select add-on")),
  /**
   * Configurator stepping actions
   */
  takeLatest(Actions.isStepShownWithId(id), onViewStep),
  takeLatest(Actions.isNextStepWithId(id), onNextStep),
  /**
   * Fetching states
   */
  takeLatest(fetchLensTypesSuccess.type, () => onFetchLensTypesSuccess(id))
];
function* listenForConfigurationChanges(id) {
  yield all([...getConfigurationChangesSagas(id), ...getTealiumSagas(id)]);
}
export {
  getConfigurationChangesSagas,
  listenForConfigurationChanges,
  onFetchLensTypesSuccess,
  onFileUploadResult,
  onPushEvent,
  onUpdatePrescriptionType
};
//# sourceMappingURL=sagas.js.map
